import {
  AssetClassLevel,
  type DriftCalculationMethod,
  Exchanges,
  type FinancialProductState,
  FinancialProductStrategy,
  type PortfolioTemplateState,
  type ThemeState,
} from '@generated/graphql';
import { CurrencyCodes, type SecurityTypes } from '@onevesthq/ov-enums';
import type { Organization } from './organization';
import type { TranslatedString } from './shared';

export const MODEL_PORTFOLIO_STRATEGIES: FinancialProductStrategy[] = [
  FinancialProductStrategy.Standard,
  FinancialProductStrategy.AssetClass,
];

/** Maps each AssetClassLevel to the corresponding FinancialProduct property key. */
export const ASSET_CLASS_FIELD_BY_LEVEL: Record<
  AssetClassLevel,
  'primaryAssetClass' | 'secondaryAssetClass' | 'tertiaryAssetClass'
> = {
  [AssetClassLevel.Primary]: 'primaryAssetClass',
  [AssetClassLevel.Secondary]: 'secondaryAssetClass',
  [AssetClassLevel.Tertiary]: 'tertiaryAssetClass',
};

export const EXCHANGE_COUNTRIES = {
  US: 'US',
  CA: 'CA',
};

export const EXCHANGES_COUNTRIES_MAP = {
  [Exchanges.Nasdaq.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Nyse.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Cboe.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.CboeCad.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.Cme.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Chx.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Ise.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Phlx.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Ms4X.toString()]: {
    country: EXCHANGE_COUNTRIES.US,
    currency: CurrencyCodes.USD,
  },
  [Exchanges.Tsx.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.TsxV.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.Fundserv.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.Cannex.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.Ptf.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
  [Exchanges.PrivateSecurity.toString()]: {
    country: EXCHANGE_COUNTRIES.CA,
    currency: CurrencyCodes.CAD,
  },
};

export type Theme = {
  id?: string;
  name?: string;
  description?: string;
  translatedName?: TranslatedString;
  translatedDescription?: TranslatedString;
  translatedShortDescription?: TranslatedString;
  key?: string;
  core?: boolean;
  state?: ThemeState;
  iconKey?: string;
};

export type AssetClass = {
  id?: string | null;
  key?: string;
  name?: string;
  description?: string;
  translatedName?: TranslatedString;
  translatedDescription?: TranslatedString;
  state?: string;
  riskLevel?: number;
};

export type Branch = {
  id?: string;
  percentage: number;
  /** Household weight; read-only. Equal to percentage on flat models. */
  effectivePercentage?: number | null;
  financialProduct: FinancialProduct;
  taxRank?: number;
  isCashEquivalent?: boolean;
  assetClass?: AssetClass;
};

export type AssetClassBranch = {
  id: string;
  targetPercentage: number | null;
  assetClass: AssetClass;
  color?:
    | 'supportOne'
    | 'supportTwo'
    | 'supportThree'
    | 'supportFour'
    | 'supportFive'
    | 'supportSix';
};

export type PortfolioTemplate = {
  id: string;
  name: string;
  state: PortfolioTemplateState;
  translatedName: TranslatedString | undefined;
  translatedTemplateDescription: TranslatedString | undefined;
  assetClassBranches: AssetClassBranch[] | undefined;
  createdAt: string;
  updatedAt: string;
};

export type TargetAllocation = {
  /** Legacy name — holds whichever asset class level (primary or secondary) the
   *  user selected via the "Group By" toggle, not strictly the secondary level. */
  secondaryAssetClass: AssetClass;
  min: number;
  max: number;
};

export type Schedule = {
  dates?: string[];
  nextDate?: string;
  id?: string;
  name?: string;
  associatedFinancialProductsCount?: number;
};

export type FinancialProduct = {
  id?: string;
  name?: string;
  url?: string;
  translatedUrl?: TranslatedString;
  translatedName?: TranslatedString;
  translatedPortfolioDisclaimer?: TranslatedString;
  state?: FinancialProductState;
  suggestedMinScore?: number;
  suggestedMaxScore?: number;
  lowThresholdScore?: number;
  tradeable?: boolean;
  suggestable?: boolean;
  autoUpdatePrices?: boolean;
  assignable?: boolean;
  cusip?: string | null;
  ticker?: string | null;
  exchange?: Exchanges;
  ric?: string;
  forecastedRateOfReturn?: number;
  forecastedStandardDeviation?: number;
  currentPriceCents?: number;
  originalCurrentPriceCents?: number;
  currencyCurrentPriceCents?: {
    CAD?: number;
    USD?: number;
  };
  children?: Branch[];
  theme?: Theme;
  primaryAssetClass?: AssetClass;
  secondaryAssetClass?: AssetClass;
  tertiaryAssetClass?: AssetClass;
  portfolioDescription?: string;
  translatedPortfolioDescription?: TranslatedString;
  investmentObjective?: string;
  translatedInvestmentObjective?: TranslatedString;
  translatedLiquidity?: TranslatedString;
  targetAllocations?: TargetAllocation[];
  updatedAt?: Date;
  riskReturnLevel?: number;
  reasonForUpdate?: string;
  translatedReasonForUpdate?: TranslatedString;
  isCash?: boolean;
  substituteFinancialProduct?: FinancialProduct;
  isPartial?: boolean;
  taxRank?: number | null;
  settlementDays?: number | null;
  currency?: CurrencyCodes;
  originalCurrency?: CurrencyCodes;
  translatedNickName?: TranslatedString;
  riskProfileName?: TranslatedString;
  portfolioTemplate?: PortfolioTemplate;
  zeroPercentCash?: boolean;
  strategy?: FinancialProductStrategy;
  assetClassLevel?: AssetClassLevel;
  assetClassBranches?: AssetClassBranch[];
  type?: SecurityTypes;
  schedule?: Schedule;
  isin?: string;
  volatile?: boolean;
  waivedFeePercentage?: number | null;
  multiplier?: number | null;
  fundSeries?: string | null;
  organization?: Organization;
  /**
   * Nullable on the SDL: `null` means the usage-count upstream was unavailable.
   * Callers MUST treat `null` as "unknown" (e.g. disable destructive actions),
   * never as 0.
   */
  assignedCount?: number | null;

  /**
   * Raw model-portfolio overrides. null means inherit — the effective policy
   * is resolved server-side at calculation time against the holder org, never
   * stamped onto the model.
   */
  driftCalculationMethod?: DriftCalculationMethod | null;
  driftTolerancePercent?: number | null;
};
